import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Gift, TrendingUp, Users, CheckCircle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../utils/api';

const NonProfitLanding = () => {
  const [formData, setFormData] = useState({
    organization_name: '',
    email: '',
    contact_person: '',
    tax_id: '',
    mission_statement: '',
    website_url: '',
    cyqr_username: '',
    proof_document: null,
    is_provincial_nonprofit: false,
    is_federal_charity: false,
    terms_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Matomo event tracking helper
  const trackEvent = (category, action, name) => {
    if (window._paq) window._paq.push(['trackEvent', category, action, name]);
  };

  // Benefit visibility — true = show, false = coming soon
  const benefitVisibility = [true, false, false, false, false, false, false];

  const benefits = [
    {
      icon: <Gift className="w-12 h-12" />,
      title: "Advertising",
      description: "Two months of standard advertising (individually or consecutively) completely free of charge every year to promote your cause and enhance your reach, without the provision of a credit card."
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Non-Profit Designation",
      description: (
        <>
          Your organization will be listed as a verified non-profit in our CYQR store, allowing our users to convert their earned digital platform points into financial donations to your organization.{' '}
          <Link to="/terms-nonprofit" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] text-sm hover:text-white transition-colors underline">
            *Subject to the Terms of Service
          </Link>
        </>
      )
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Cash Donation Eligibility",
      description: "Become eligible to receive instant-donations directly through the CYQR platform from our engaged community."
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Community Connection (Coming Soon)",
      description: (
        <>
          Direct access to our events calendar and ticket sales platform with all platform fees waived for tickets sold through our platform.{' '}
          <Link to="/terms-nonprofit" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] text-sm hover:text-white transition-colors underline">
            *Subject to our Terms of Service
          </Link>
        </>
      )
    },
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: "Community Badge Initiative",
      description: "Authorize your branding to be utilized as a community badge provided or earned by users for meeting specific criteria, i.e., donation of $50 or more through the platform via cash or certain points levels through the store."
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Community Profile Page",
      description: "A high-end designed organizational profile optimized for social media sharing and donation. This dedicated page serves as your digital fundraising hub, making it easy for supporters to learn about your mission, share your cause, and contribute through multiple channels."
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Community Asks",
      description: (
        <>
          A short text-only request directed to community members to be utilized in platform email correspondence and <strong>potentially</strong> utilized in Councillor correspondence to ward individuals{' '}
          <Link to="/terms-nonprofit" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] text-sm hover:text-white transition-colors underline">
            (subject to the Councillors' discretion and not guaranteed)
          </Link>
        </>
      )
    }
  ];

  const faqs = [
    {
      question: "Who is eligible for the non-profit program?",
      answer: "Any organization incorporated as a non-profit in Saskatchewan is eligible, however preference will be given to Local Regina Non-Profits and Non-profit organizations with a community connection to Regina and/or provide services or measurable impact to individuals within Regina. While federal charity status is not required, you must be a registered non-profit organization."
    },
    {
      question: "What does the free advertising include?",
      answer: "You'll receive two months of standard advertising per year on the CYQR platform. The spots are available for a month per ad (i.e. split into weeks) and heavily discounted ad space if needed. Although we hope to give every applicant their preferred ad-slots, in some instances the platform may limit availability on specific months due to existing advertising commitments or to prevent over saturation of competing campaigns (i.e. Christmas Donation Drives)."
    },
    {
      question: "How do donations work?",
      answer: "Once approved, your organization will be listed as donation-eligible. Community members can convert their earned points to direct cash donations to your organization and/OR provide payment directly through the platform to your organization, as a reoccurring monthly contribution or as a one-time donation payment."
    },
    {
      question: "How long does the approval process take?",
      answer: "Most applications are reviewed within 3-5 business days. We'll contact you via the email provided if we need any additional information."
    },
    {
      question: "Is there any cost to join?",
      answer: "Absolutely not! This program is completely free for all qualifying non-profit organizations in Saskatchewan."
    }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.organization_name || !formData.email || !formData.contact_person) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.terms_accepted) {
      toast.error('You must accept the Terms of Service to proceed');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('organization_name', formData.organization_name);
      submitData.append('email', formData.email);
      submitData.append('contact_person', formData.contact_person);
      submitData.append('tax_id', formData.tax_id || '');
      submitData.append('mission_statement', formData.mission_statement || '');
      submitData.append('website_url', formData.website_url || '');
      submitData.append('cyqr_username', formData.cyqr_username || '');
      submitData.append('is_provincial_nonprofit', formData.is_provincial_nonprofit.toString());
      submitData.append('is_federal_charity', formData.is_federal_charity.toString());
      submitData.append('terms_accepted', formData.terms_accepted.toString());
      if (formData.proof_document) submitData.append('proof_document', formData.proof_document);

      await api.post('/nonprofit/register', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });

      trackEvent('NonProfit', 'Registration', formData.organization_name);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (index) => setExpandedFaq(expandedFaq === index ? null : index);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Sticky Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrollY > 50 ? 'bg-black/95 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
        <div className="max-w-[1800px] mx-auto px-8 lg:px-20 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_donate-forward-4/artifacts/0p3g8dso_cyqr_logo.png"
              alt="CYQR"
              className="h-10 object-contain"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-10">
            <a href="#benefits" className="text-white/50 hover:text-[#FFD700] transition-colors text-sm tracking-[0.15em]">BENEFITS</a>
            <a href="#register" className="text-white/50 hover:text-[#FFD700] transition-colors text-sm tracking-[0.15em]">REGISTER</a>
            <a href="#faq" className="text-white/50 hover:text-[#FFD700] transition-colors text-sm tracking-[0.15em]">FAQ</a>
            <Link to="/" className="text-white/30 hover:text-white transition-colors text-xs tracking-widest border border-white/10 px-4 py-2 hover:border-white/30">
              BACK TO CYQR
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1665586645771-93dea5d08eda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxSZWdpbmElMjBjaXR5c2NhcGV8ZW58MHx8fHwxNzc1NTUwMDg2fDA&ixlib=rb-4.1.0&q=85)',
              transform: `translateY(${scrollY * 0.4}px)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/50" />
        </div>

        <div className="relative z-10 max-w-[1800px] mx-auto px-8 lg:px-20 pt-32 pb-20">
          <div className="max-w-5xl">
            <div className="inline-flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-[#FFD700]" />
              <span className="text-[#FFD700] text-sm font-medium tracking-[0.2em] uppercase">Community First</span>
            </div>
            <h1 className="text-7xl lg:text-8xl xl:text-9xl font-bold mb-8 leading-[0.9] tracking-tight">
              WE LOVE
              <br />
              <span className="text-[#FFD700] italic font-light">NON-PROFITS</span>
            </h1>
            <p className="text-2xl lg:text-3xl text-white/90 mb-4 max-w-3xl leading-relaxed font-light">
              Join our ecosystem engineered for high-impact civic engagement.
            </p>
            <p className="text-xl lg:text-2xl text-white/60 mb-12 max-w-3xl leading-relaxed">
              To assist your organization in weathering the storm, we supplement your campaigns or goals with{' '}
              <span className="text-[#FFD700]">two months of standard advertising</span> free of charge and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <a
                href="#register"
                onClick={() => trackEvent('NonProfit', 'CTA Click', 'Hero Register Now')}
                className="group bg-[#FFD700] text-black px-10 py-6 text-lg font-medium hover:bg-white transition-all duration-300 inline-flex items-center justify-between gap-4 relative overflow-hidden"
                data-testid="nonprofit-register-cta"
              >
                <span className="relative z-10">REGISTER NOW</span>
                <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-white transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" />
              </a>
              <a
                href="#benefits"
                className="group bg-white/5 backdrop-blur-sm border border-white/20 text-white px-10 py-6 text-lg font-medium hover:bg-white/10 hover:border-[#FFD700] transition-all duration-300 inline-flex items-center justify-between gap-4"
              >
                <span>LEARN MORE</span>
                <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/30" />
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: 'url(https://images.pexels.com/photos/6647027/pexels-photo-6647027.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />
        </div>
        <div className="relative z-10 max-w-[1800px] mx-auto px-8 lg:px-20">
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#FFD700]" />
              <span className="text-[#FFD700] text-sm font-medium tracking-[0.2em] uppercase">The Program</span>
            </div>
            <h2 className="text-6xl lg:text-7xl font-bold mb-6 max-w-4xl">
              THE HEART OF<br />
              <span className="text-[#FFD700] italic font-light">THE CITY.</span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl leading-relaxed">
              We believe in the power of community and both the impact and necessity of our local non-profit organizations. Here's what you get when you join.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-12 hover:border-[#FFD700]/50 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className={`text-[#FFD700] mb-6 transform group-hover:scale-110 transition-transform duration-500 ${!benefitVisibility[index] ? 'opacity-50' : ''}`}>
                    {benefit.icon}
                  </div>
                  <h3 className="text-3xl font-bold mb-4 tracking-tight">{benefit.title}</h3>
                  <div className={`text-lg text-white/70 leading-relaxed ${!benefitVisibility[index] ? 'blur-sm select-none' : ''}`}>
                    {benefit.description}
                  </div>
                </div>
                {!benefitVisibility[index] && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <div className="bg-black/80 backdrop-blur-sm border-2 border-[#FFD700] px-8 py-4">
                      <span className="text-[#FFD700] text-2xl font-bold tracking-wider">COMING SOON</span>
                    </div>
                  </div>
                )}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFD700]/5 transform translate-x-16 -translate-y-16 rotate-45 group-hover:bg-[#FFD700]/10 transition-colors duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/98 to-black" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-8 lg:px-20">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#FFD700]" />
              <span className="text-[#FFD700] text-sm font-medium tracking-[0.2em] uppercase">Apply Now</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold mb-6">
              REGISTER YOUR<br />
              <span className="text-[#FFD700] italic font-light">ORGANIZATION</span>
            </h2>
            <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
              Fill out the form below to apply for the CYQR Non-Profit Program. We'll review your application and get back to you within 3-5 business days.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-20 border border-[#FFD700]/30 bg-[#FFD700]/5">
              <CheckCircle className="w-16 h-16 text-[#FFD700] mx-auto mb-6" />
              <h3 className="text-4xl font-bold text-white mb-4">APPLICATION SUBMITTED!</h3>
              <p className="text-white/70 text-lg max-w-md mx-auto">We'll review your application and contact you at the email provided within 3-5 business days.</p>
              <Link to="/" className="inline-block mt-8 text-[#FFD700] hover:text-white text-sm tracking-widest underline transition-colors">BACK TO CYQR</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="relative" data-testid="nonprofit-form">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-white/[0.02] to-transparent backdrop-blur-sm border border-white/10" />
              <div className="relative z-10 p-10 lg:p-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Organization Name <span className="text-[#FFD700]">*</span></label>
                    <input type="text" name="organization_name" value={formData.organization_name} onChange={handleChange} required placeholder="Enter organization name" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" data-testid="nonprofit-org-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Email Address <span className="text-[#FFD700]">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="contact@example.org" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" data-testid="nonprofit-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Contact Person <span className="text-[#FFD700]">*</span></label>
                    <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} required placeholder="Full name" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" data-testid="nonprofit-contact" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Tax ID / Registration Number</label>
                    <input type="text" name="tax_id" value={formData.tax_id} onChange={handleChange} placeholder="Optional" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Mission Statement</label>
                    <textarea name="mission_statement" value={formData.mission_statement} onChange={handleChange} rows={4} placeholder="Tell us about your organization's mission (optional)" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors resize-none placeholder:text-white/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Website / Social Media URL</label>
                    <input type="url" name="website_url" value={formData.website_url} onChange={handleChange} placeholder="https://yourwebsite.org" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">CYQR User Account Name</label>
                    <input type="text" name="cyqr_username" value={formData.cyqr_username} onChange={handleChange} placeholder="Your CYQR username" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors placeholder:text-white/30" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 tracking-wide uppercase text-white/80">Proof of Non-Profit Status</label>
                    <input type="file" id="proof_document" name="proof_document" onChange={handleChange} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="w-full bg-black/50 border-b-2 border-white/20 px-0 py-4 text-white text-lg focus:outline-none focus:border-[#FFD700] transition-colors file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-[#FFD700] file:text-black hover:file:bg-white file:cursor-pointer file:transition-colors" />
                    <p className="text-sm text-white/40 mt-3">Upload your incorporation documents or charity registration (PDF, DOC, or image files)</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-4 tracking-wide uppercase text-white/80">Organization Type</label>
                    <div className="space-y-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="is_provincial_nonprofit" checked={formData.is_provincial_nonprofit} onChange={handleChange} className="mt-1 w-5 h-5 accent-[#FFD700] cursor-pointer" />
                        <span className="text-white/70 text-base leading-relaxed">We are a provincially registered non-profit organization</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="is_federal_charity" checked={formData.is_federal_charity} onChange={handleChange} className="mt-1 w-5 h-5 accent-[#FFD700] cursor-pointer" />
                        <span className="text-white/70 text-base leading-relaxed">We are a federally registered Non-profit with Charity Status</span>
                      </label>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleChange} required className="mt-1 w-5 h-5 accent-[#FFD700] cursor-pointer" data-testid="nonprofit-terms-checkbox" />
                      <span className="text-white/70 text-base leading-relaxed">
                        I am authorized by the above noted organization to submit this application and to bind the organization in acceptance of the{' '}
                        <Link to="/terms-nonprofit" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:text-white transition-colors underline">Non-Profit Program Terms of Service</Link>. <span className="text-[#FFD700]">*</span>
                      </span>
                    </label>
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="nonprofit-submit-btn" className="group w-full bg-[#FFD700] text-black px-10 py-6 text-lg font-bold hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-4 relative overflow-hidden tracking-wide uppercase">
                  <span className="relative z-10">{loading ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}</span>
                  {!loading && <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-2 transition-transform" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />
        <div className="relative z-10 max-w-5xl mx-auto px-8 lg:px-20">
          <div className="mb-16 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#FFD700]" />
              <span className="text-[#FFD700] text-sm font-medium tracking-[0.2em] uppercase">Questions?</span>
              <div className="h-px w-12 bg-[#FFD700]" />
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold mb-6">
              FREQUENTLY ASKED<br />
              <span className="text-[#FFD700] italic font-light">QUESTIONS</span>
            </h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 overflow-hidden hover:border-[#FFD700]/30 transition-all duration-300">
                <button onClick={() => toggleFaq(index)} className="w-full px-8 py-7 flex items-center justify-between text-left">
                  <span className="text-xl font-medium pr-8">{faq.question}</span>
                  <div className="flex-shrink-0">
                    {expandedFaq === index ? <ChevronUp className="w-6 h-6 text-[#FFD700]" /> : <ChevronDown className="w-6 h-6 text-white/50 group-hover:text-[#FFD700] transition-colors" />}
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-8 pb-7 pt-2 border-t border-white/10">
                    <p className="text-white/70 leading-relaxed text-lg">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 border-t border-white/10">
        <div className="max-w-[1800px] mx-auto px-8 lg:px-20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <img src="https://customer-assets.emergentagent.com/job_donate-forward-4/artifacts/zmzp0np3_CYQR-Icon-128.png" alt="CYQR Icon" className="h-10 w-10 object-contain opacity-50" />
              <div>
                <div className="text-white/40 text-sm tracking-wide">© 2026 CYQR.ca</div>
                <div className="text-white/20 text-xs tracking-wide">Show Them Your Regina</div>
              </div>
            </div>
            <div className="flex gap-10 text-white/40 text-sm">
              <Link to="/terms" className="hover:text-[#FFD700] transition-colors tracking-wide">PRIVACY POLICY</Link>
              <Link to="/terms" className="hover:text-[#FFD700] transition-colors tracking-wide">TERMS OF SERVICE</Link>
              <Link to="/terms-nonprofit" className="hover:text-[#FFD700] transition-colors tracking-wide">NON-PROFIT TERMS</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NonProfitLanding;
